import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DashboardStore } from '../../stores/DashboardStore';
import { getUseCaseService } from '../../services/UseCaseService';
import type { ImpactPlusReport } from '../../entities/report/schemas/ImpactPlusReport.schema';
import {
  countStakeholderRegisterStats,
  type StakeholderCategory,
  type StakeholderRegisterRow,
  type StakeholderRegisterSection,
} from './stakeholderRegisterData';

function displayUseCaseTitle(title: string | undefined): string {
  const t = String(title ?? '').trim();
  if (!t || t === 'N/A' || t === '<untitled>') return 'Untitled use case';
  return t;
}

type FilterKey = 'all' | StakeholderCategory;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'people', label: 'People' },
  { key: 'organisation', label: 'Organisation' },
  { key: 'ecosystem', label: 'Ecosystem' },
];


const CATEGORY_META: Record<StakeholderCategory, { title: string; description: string }> = {
  people: {
    title: 'People Stakeholders',
    description: 'Individuals and groups directly or indirectly affected by the AI system - as users, subjects of decisions, or those impacted by downstream effects.',
  },
  organisation: {
    title: 'Organisation Stakeholders',
    description: 'Legal entities - companies, regulatory bodies, internal teams, or institutional partners - whose operations, obligations, or reputations are affected by the AI system.',
  },
  ecosystem: {
    title: 'Ecosystem Stakeholders',
    description: 'Broader systemic actors - industries, markets, society - where the impact is diffuse, collective, or long-term rather than affecting a specific individual or entity.',
  },
};

const CATEGORY_ORDER: StakeholderCategory[] = ['people', 'organisation', 'ecosystem'];

function buildRegisterSections(report: ImpactPlusReport | null): StakeholderRegisterSection[] {
  if (!report) return [];
  const cohorts = report.impactedStakeholders.impactedCohorts;
  return CATEGORY_ORDER
    .map((cat) => {
      const rows: StakeholderRegisterRow[] = cohorts
        .filter((c) => c.stakeholderCategory === cat)
        .map((c) => ({
          group: c.label,
          stakeholderType: (cat === 'people' ? 'Direct' : 'Indirect') as 'Direct' | 'Indirect',
          vulnerabilityLabel: c.cohortRisk,
          power: 'N/A',
          description: c.description,
        }));
      return { id: cat, ...CATEGORY_META[cat], rows };
    })
    .filter((s) => s.rows.length > 0);
}


function riskDotColor(label: string): string {
  switch (label.toLowerCase()) {
    case 'critical': return 'var(--critical)';
    case 'high': return 'var(--high)';
    case 'moderate': return 'var(--moderate)';
    case 'low':
    case 'negligible':
    default: return 'var(--low)';
  }
}

function VulnerabilityCell({ label }: { label: string | null }) {
  if (!label) {
    return <span className="text-sm text-gray-500">None</span>;
  }
  return (
    <span className="inline-flex items-center gap-2 rounded-md bg-[#F2F2F2] px-3 py-1 text-xxs font-semibold text-[#000E28]">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: riskDotColor(label) }} />
      {label[0].toUpperCase() + label.slice(1).toLowerCase()}
    </span>
  );
}

export default function ImpactPlusStakeholderRegister() {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const [searchParams] = useSearchParams();
  const impactPlusReportId = searchParams.get('impactPlusReportId') ?? '';
  const impactPlusReportQuery = impactPlusReportId
    ? `?impactPlusReportId=${encodeURIComponent(impactPlusReportId)}`
    : '';
  const [selectedFilters, setSelectedFilters] = useState<Set<StakeholderCategory>>(() => new Set());
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());

  const useCaseBriefs = DashboardStore.useDashboardStore((s) => s.useCaseBriefs);

  const [impactPlusReport, setImpactPlusReport] = useState<ImpactPlusReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const loadImpactPlusReport = useCallback(async () => {
    if (!impactPlusReportId) {
      setImpactPlusReport(null);
      setReportError(null);
      setIsLoadingReport(false);
      return;
    }
    setReportError(null);
    setIsLoadingReport(true);
    try {
      const report = await getUseCaseService().getImpactPlusReport(impactPlusReportId);
      setImpactPlusReport(report);
    } catch (err) {
      setImpactPlusReport(null);
      setReportError(err instanceof Error ? err.message : 'Could not load Impact+ report.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [impactPlusReportId]);

  useEffect(() => {
    void loadImpactPlusReport();
  }, [loadImpactPlusReport]);

  const sections = useMemo(() => buildRegisterSections(impactPlusReport), [impactPlusReport]);

  const stats = useMemo(() => countStakeholderRegisterStats(sections), [sections]);

  const useCaseTitle = useMemo(() => {
    const brief = useCaseBriefs.find((b) => b.useCaseChatId === useCaseChatId);
    const reportTitle = impactPlusReport?.titlePage?.useCaseTitle;
    return displayUseCaseTitle(reportTitle || brief?.useCaseTitle);
  }, [useCaseBriefs, useCaseChatId, impactPlusReport]);

  const visibleSections = useMemo(() => {
    if (selectedFilters.size === 0) return sections;
    return sections.filter((s) => selectedFilters.has(s.id as StakeholderCategory));
  }, [selectedFilters, sections]);

  const tophazardsHref = `/use-case/${encodeURIComponent(useCaseChatId)}/hazards/${impactPlusReportQuery}`;
  const impactAssessmentHref = `/use-case/${encodeURIComponent(useCaseChatId)}/impact-assessment${impactPlusReportQuery}`;

  if (!useCaseChatId) return null;

  return (
    <div className="min-h-screen bg-impact-plus-page pb-12 pt-2">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/dashboard"
            className="line-height-normal flex items-center gap-2 text-sm font-medium text-royal-blue hover:text-deep-blue"
          >
            <div style={{ lineHeight: '0.5' }}>←</div><div className="hover:underline">Use Case Register</div>
          </Link>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to={tophazardsHref}
              className="text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              ← Impact Overview
            </Link>
            <Link
              to={impactAssessmentHref}
              className="text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Stakeholder Impact Assessment →
            </Link>
          </div>
        </div>

        <div className="mb-5">
          <div className="flex items-center gap-[14px]">
            <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] bg-soft-gray">
              <span className="aq-icon text-midnight-navi text-[23px]" aria-hidden>
                groups
              </span>
            </div>
            <h1 className="min-w-0 flex-1 text-[21px] leading-[1.2] tracking-[-0.3px] font-semibold text-midnight-navi">
              Stakeholder Register
            </h1>
          </div>
          <p className="mt-1 pl-[52px] text-xs text-gray-500">
            {useCaseTitle} · {stats.total} stakeholder groups identified
          </p>
        </div>

        {impactPlusReportId && isLoadingReport && !impactPlusReport && (
          <div className="mb-6 animate-pulse space-y-4">
            <div className="h-32 rounded-xl bg-gray-100" />
            <div className="h-32 rounded-xl bg-gray-100" />
          </div>
        )}

        {impactPlusReportId && !isLoadingReport && reportError && !impactPlusReport && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-xs"
            role="alert"
          >
            <div className="font-semibold">Could not load Impact+ report</div>
            <div className="mt-1">{reportError}</div>
            <button
              type="button"
              onClick={() => void loadImpactPlusReport()}
              className="mt-3 text-sm font-medium text-royal-blue hover:text-deep-blue hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {!impactPlusReportId && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500 shadow-xs">
            No Impact+ report linked to this use case.
          </div>
        )}

        {impactPlusReport && sections.length === 0 && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500 shadow-xs">
            No stakeholder groups identified in this report.
          </div>
        )}

        {sections.length > 0 && (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Show:</span>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map(({ key, label }) => {
                  const active = key === 'all'
                    ? selectedFilters.size === 0
                    : selectedFilters.has(key as StakeholderCategory);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        if (key === 'all') {
                          setSelectedFilters(new Set());
                        } else {
                          setSelectedFilters((prev) => {
                            const next = new Set(prev);
                            if (next.has(key as StakeholderCategory)) {
                              next.delete(key as StakeholderCategory);
                            } else {
                              next.add(key as StakeholderCategory);
                            }
                            return next;
                          });
                        }
                      }}
                      className={`rounded-[6px] px-3.5 py-1.5 text-xs font-semibold transition-colors ${active
                        ? 'bg-slate-gray text-deep-blue shadow-sm'
                        : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      aria-pressed={active}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-10">
              {visibleSections.map((section) => (
                <section
                  key={section.id}
                  aria-labelledby={`stakeholder-section-${section.id}`}
                >
                  <div className="border-b border-gray-100 py-3 md:py-2">
                    <h2
                      id={`stakeholder-section-${section.id}`}
                      className="text-base font-bold text-gray-900 md:text-lg"
                    >
                      {section.title.replace(/\s*stakeholders?$/i, '')} · {section.rows.length}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      {section.description}
                    </p>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200/90 bg-white shadow-xs">
                    <table className="w-full min-w-[560px] table-fixed border-collapse text-left">
                      <thead className="bg-impact-plus-page">
                        <tr className="border-b border-gray-200 bg-[var(--table-header-background)]">
                          <th className="w-[50%] py-3 pl-5 pr-3 text-xxxs font-semibold uppercase tracking-wider text-gray-400 md:pl-6">
                            Stakeholder group
                          </th>
                          <th className="w-[16%] py-3 px-3 text-xxxs font-semibold uppercase tracking-wider text-gray-400">
                            Stakeholder type
                          </th>
                          <th className="w-[34%] py-3 px-3 text-xxxs font-semibold uppercase tracking-wider text-gray-400">
                            Cohort Risk
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.rows.map((row, idx) => {
                          const id = `${section.id}__${row.group}`;
                          const isExpanded = expandedRowIds.has(id);
                          const rowBg = idx % 2 === 1
                            ? 'bg-[var(--table-header-background)]'
                            : 'bg-white';
                          return (
                            <Fragment key={id}>
                              <tr
                                className={`cursor-pointer select-none ${!isExpanded ? 'border-b border-gray-200 last:border-0' : ''} ${rowBg}`}
                                onClick={() =>
                                  setExpandedRowIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(id)) next.delete(id);
                                    else next.add(id);
                                    return next;
                                  })
                                }
                                aria-expanded={isExpanded}
                              >
                                <td className={`${isExpanded ? 'py-3' : 'py-4'} pl-5 pr-3 align-top md:pl-6`}>
                                  <span className="inline-flex items-center gap-2">
                                    <ChevronRight
                                      className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''
                                        }`}
                                      strokeWidth={2.5}
                                      aria-hidden
                                    />
                                    <span className="text-xs font-semibold text-gray-900">{row.group}</span>
                                  </span>
                                </td>
                                <td className={`${isExpanded ? 'py-3' : 'py-4'} px-3 align-top`}>
                                  <span className="text-xs text-gray-700">{row.stakeholderType}</span>
                                </td>
                                <td className={`${isExpanded ? 'py-3' : 'py-4'} px-3 align-middle`}>
                                  <VulnerabilityCell label={row.vulnerabilityLabel} />
                                </td>
                              </tr>

                              {isExpanded ? (
                                <tr className="border-b border-gray-200 last:border-0">
                                  <td
                                    colSpan={3}
                                    className="px-5 pb-4 pt-1 md:pl-6 md:pr-6"
                                    role="region"
                                    aria-label={`Details for ${row.group}`}
                                  >
                                    <div className="rounded-lg border border-gray-200 bg-info-bg px-4 py-3 text-xs leading-relaxed text-gray-700">
                                      {row.description?.trim()
                                        ? row.description
                                        : 'No description available.'}
                                    </div>
                                  </td>
                                </tr>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
